import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  TablePagination,
  TableSortLabel,
  Tooltip,
  Box,
  Checkbox,
  Typography,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LockReset as ResetPasswordIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { UserListItem, AccountStatus, OrgRole } from "@shared/schemas";
import { formatDate } from "../../utils/dateUtils";
import {
  getRoleTooltip,
  getStatusTooltip,
  getRoleColor,
  getStatusColor,
} from "../../utils/badges";

// Types for sort column and direction
type OrderDirection = "asc" | "desc";
type OrderByColumn = "firstName" | "lastName" | "email" | "role" | "createdAt";

// Props interface
interface UserTableProps {
  users: UserListItem[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  orderBy: OrderByColumn;
  orderDirection: OrderDirection;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onSortChange: (
    orderBy: OrderByColumn,
    orderDirection: OrderDirection,
  ) => void;
  onEdit: (user: UserListItem) => void;
  onDelete: (user: UserListItem) => void;
  onResetPassword: (user: UserListItem) => void;
  onView: (user: UserListItem) => void;
  onSendOnboardingEmail?: (user: UserListItem) => void;
  bulkMode?: boolean;
  selectedUserIds?: string[];
  onSelectionChange?: (selectedUserIds: string[]) => void;
}

/**
 * Component for displaying a table of users with sorting, pagination, and actions
 */
const UserTable: React.FC<UserTableProps> = ({
  users,
  totalCount,
  page,
  rowsPerPage,
  orderBy,
  orderDirection,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onEdit,
  onDelete,
  onResetPassword,
  onView,
  onSendOnboardingEmail,
  bulkMode = false,
  selectedUserIds = [],
  onSelectionChange,
}) => {
  // Menu state for action dropdown
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);

  // Check if all current page users are selected
  const allSelected = useMemo(() => {
    return (
      bulkMode &&
      users.length > 0 &&
      users.every((user) => selectedUserIds.includes(user.id))
    );
  }, [bulkMode, users, selectedUserIds]);

  // Check if some but not all current page users are selected
  const someSelected = useMemo(() => {
    return (
      bulkMode &&
      users.some((user) => selectedUserIds.includes(user.id)) &&
      !allSelected
    );
  }, [bulkMode, users, selectedUserIds, allSelected]);

  // Handle select all checkbox
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionChange) return;

    if (event.target.checked) {
      // Add all current page user IDs to selection
      const pageUserIds = users.map((user) => user.id);
      const newSelection = Array.from(
        new Set([...selectedUserIds, ...pageUserIds]),
      );
      onSelectionChange(newSelection);
    } else {
      // Remove all current page user IDs from selection
      const pageUserIds = new Set(users.map((user) => user.id));
      const newSelection = selectedUserIds.filter((id) => !pageUserIds.has(id));
      onSelectionChange(newSelection);
    }
  };

  // Handle individual row selection
  const handleSelectRow = (userId: string) => {
    if (!onSelectionChange) return;

    if (selectedUserIds.includes(userId)) {
      onSelectionChange(selectedUserIds.filter((id) => id !== userId));
    } else {
      onSelectionChange([...selectedUserIds, userId]);
    }
  };

  // Handle menu open
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    user: UserListItem,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // Handle menu actions
  const handleEdit = () => {
    if (selectedUser) {
      onEdit(selectedUser);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedUser) {
      onDelete(selectedUser);
    }
    handleMenuClose();
  };

  const handleResetPassword = () => {
    if (selectedUser) {
      onResetPassword(selectedUser);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedUser) {
      onView(selectedUser);
    }
    handleMenuClose();
  };

  // Handle sort change
  const handleSortRequest = (property: OrderByColumn) => {
    const isAsc = orderBy === property && orderDirection === "asc";
    onSortChange(property, isAsc ? "desc" : "asc");
  };

  // Handle pagination change
  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onRowsPerPageChange(parseInt(event.target.value, 10));
  };

  // Render status chip with appropriate color and tooltip
  const renderStatusChip = (status: AccountStatus) => {
    return (
      <Tooltip title={getStatusTooltip(status)} placement="top">
        <Chip
          label={status}
          color={getStatusColor(status)}
          size="small"
          sx={{ fontSize: "0.7rem", height: "20px", cursor: "help" }}
        />
      </Tooltip>
    );
  };

  // Render role chip with appropriate color and tooltip
  const renderRoleChip = (role: OrgRole | string) => {
    return (
      <Tooltip title={getRoleTooltip(role as OrgRole)} placement="top">
        <Chip
          label={role}
          color={getRoleColor(role as OrgRole)}
          size="small"
          sx={{ fontSize: "0.7rem", height: "20px", cursor: "help" }}
        />
      </Tooltip>
    );
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer>
        <Table aria-label="users table">
          <TableHead>
            <TableRow>
              {bulkMode && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={handleSelectAll}
                    inputProps={{
                      "aria-label": "select all users",
                    }}
                  />
                </TableCell>
              )}
              <TableCell>
                <TableSortLabel
                  active={orderBy === "firstName"}
                  direction={orderBy === "firstName" ? orderDirection : "asc"}
                  onClick={() => handleSortRequest("firstName")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? orderDirection : "asc"}
                  onClick={() => handleSortRequest("email")}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "role"}
                  direction={orderBy === "role" ? orderDirection : "asc"}
                  onClick={() => handleSortRequest("role")}
                >
                  Role
                </TableSortLabel>
              </TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Onboarding</TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? orderDirection : "asc"}
                  onClick={() => handleSortRequest("createdAt")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => {
              const isSelected = selectedUserIds.includes(user.id);

              return (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => !bulkMode && onView(user)}
                  selected={isSelected}
                  sx={{
                    cursor: bulkMode ? "default" : "pointer",
                    "&:last-child td, &:last-child th": { border: 0 },
                    backgroundColor: isSelected ? "action.selected" : "inherit",
                  }}
                >
                  {bulkMode && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isSelected}
                        onChange={() => handleSelectRow(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        inputProps={{
                          "aria-labelledby": `user-${user.id}-name`,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell
                    component="th"
                    scope="row"
                    id={`user-${user.id}-name`}
                  >
                    {`${user.firstName} ${user.lastName}`}
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {renderRoleChip(
                      user.organizationRole || user.role || "MEMBER",
                    )}
                  </TableCell>
                  <TableCell>{renderStatusChip(user.accountStatus)}</TableCell>
                  <TableCell>
                    {/* Onboarding Actions */}
                    {user.accountStatus === "CREATED" &&
                      onSendOnboardingEmail && (
                        <Tooltip title="Send onboarding email" placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSendOnboardingEmail(user);
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    {user.accountStatus === "ONBOARDING" &&
                      onSendOnboardingEmail && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <Tooltip
                            title={
                              user.onboardingEmailSentAt
                                ? `Onboarding email sent on ${formatDate(user.onboardingEmailSentAt)}`
                                : "Onboarding email sent"
                            }
                            placement="top"
                          >
                            <Chip
                              label="Sent"
                              size="small"
                              color="primary"
                              sx={{ fontSize: "0.7rem", height: "20px" }}
                            />
                          </Tooltip>
                          <Tooltip
                            title="Resend onboarding email"
                            placement="top"
                          >
                            <IconButton
                              size="small"
                              color="default"
                              onClick={(e) => {
                                e.stopPropagation();
                                onSendOnboardingEmail(user);
                              }}
                            >
                              <RefreshIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    {(user.accountStatus === "ACTIVE" ||
                      user.accountStatus === "SUSPENDED" ||
                      user.accountStatus === "LOCKED" ||
                      user.accountStatus === "ARCHIVED") && (
                      <Typography variant="caption" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                    {user.accountStatus === "UNVERIFIED" && (
                      <Typography variant="caption" color="text.secondary">
                        Legacy
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>
                    {user.lastLoginAt ? formatDate(user.lastLoginAt) : "Never"}
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.5,
                      }}
                    >
                      <Tooltip title="Edit" placement="top">
                        <IconButton
                          aria-label="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(user);
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        aria-label="more actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuOpen(e, user);
                        }}
                        size="small"
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleResetPassword}>
          <ResetPasswordIcon fontSize="small" sx={{ mr: 1 }} />
          Reset Password
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default UserTable;
